import {
  type Country,
  type FullDemandSignalDTO,
  type FullSupplySignalDTO,
  Incoterms,
  type MatchedSiteMaterialV2,
  type PaginatedMatchedSiteMaterialV2List,
  type PricedDemandSignalDTO,
  type PricedSupplySignalDTO,
  useApiTradeV1DemandSignalsCalculateOptimalPriceCreate,
  useApiTradeV1DemandSignalsCalculateSellPriceCreate,
  useApiTradeV1SupplySignalCalculateBuyPriceCreate,
  useApiTradeV2DemandSignalsBulkMatchSiteMaterialsList,
} from 'api';
import { logger } from 'monitor';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  DEFAULT_NUMBER_OF_LOADS,
  DEFAULT_PAYLOAD_SIZE,
  DEFAULT_RECENT_DEALS_LOOKBACK_DAYS,
  DEFAULT_RECENT_OFFERS_LOOKBACK_DAYS,
  DEFAULT_RECENT_SIGNALS_LOOKBACK_DAYS,
  DEFAULT_TARGET_MARGIN,
  MAX_BUYER_PRICE_DIFFERENCE_PERCENTAGE,
  MAX_SELLER_PRICE_DIFFERENCE_PERCENTAGE,
  PAGE_SIZE,
} from '../../constants';

export type MatchedSiteMaterialWithSignals = {
  siteMaterial: MatchedSiteMaterialV2;
  demandSignals: FullDemandSignalDTO[];
};

export type SiteMaterialDemandSignalMatch = {
  demandSignal: FullDemandSignalDTO;
  supplySignal?: FullSupplySignalDTO;
  siteMaterial: MatchedSiteMaterialV2;
  targetMargin?: number;
  sellerOffer?: PricedDemandSignalDTO;
  buyerOffer?: PricedSupplySignalDTO;
};

export type MatchedMaterialWithOffers = {
  siteMaterial: MatchedSiteMaterialV2;
  offers: SiteMaterialDemandSignalMatch[];
  isPending: boolean;
};

export const transformPaginatedMatchedSiteMaterial = (
  siteMaterials: PaginatedMatchedSiteMaterialV2List | undefined,
  demandSignals?: FullDemandSignalDTO[]
): MatchedSiteMaterialWithSignals[] => {
  if (!siteMaterials?.results || !demandSignals?.length) {
    return [];
  }

  const demandSignalMap = new Map(demandSignals.map((signal) => [signal.id, signal]));

  return siteMaterials.results.reduce((acc: MatchedSiteMaterialWithSignals[], siteMaterial: MatchedSiteMaterialV2) => {
    const matchedDemandSignals: FullDemandSignalDTO[] = [];

    siteMaterial.demand_signal_ids?.forEach((demandSignalId: number) => {
      const demandSignal = demandSignalMap.get(demandSignalId);
      if (!demandSignal) {
        throw new Error(`Demand signal not found for id ${demandSignalId}`);
      }
      matchedDemandSignals.push(demandSignal);
    });

    if (matchedDemandSignals.length > 0) {
      acc.push({
        demandSignals: matchedDemandSignals,
        siteMaterial,
      });
    }

    return acc;
  }, []);
};

export const sortOffersByPriceDelta = (a: SiteMaterialDemandSignalMatch, b: SiteMaterialDemandSignalMatch): number => {
  // Prioritize items with supply signals
  if (a.supplySignal && !b.supplySignal) return -1; // a comes first
  if (!a.supplySignal && b.supplySignal) return 1; // b comes first

  // Deprioritize supply signals with price of 1 (placeholder price)
  if (a.supplySignal?.price_per_tonne === '1' && b.supplySignal?.price_per_tonne !== '1') return 1; // b comes first
  if (a.supplySignal?.price_per_tonne !== '1' && b.supplySignal?.price_per_tonne === '1') return -1; // a comes first

  // If both have supply signals or both don't, check price details
  if (!a.sellerOffer?.price_details && !b.sellerOffer?.price_details) return 0;
  if (!a.sellerOffer?.price_details) return 1; // b comes first
  if (!b.sellerOffer?.price_details) return -1; // a comes first

  // If both have supply signals, calculate price deltas
  if (a.supplySignal && b.supplySignal) {
    const aDelta = Math.abs(
      Number(a.sellerOffer.price_details.seller_price_per_tonne) - Number(a.supplySignal.price_per_tonne || 0)
    );
    const bDelta = Math.abs(
      Number(b.sellerOffer.price_details.seller_price_per_tonne) - Number(b.supplySignal.price_per_tonne || 0)
    );
    return aDelta - bDelta;
  }

  return 0;
};
type MatchedMaterialsResponse = Omit<PaginatedMatchedSiteMaterialV2List, 'results'> & {
  results: MatchedSiteMaterialWithSignals[];
};

export const useMatchedMaterialsWithOffers = ({
  demandSignals,
  accountManagerIds,
  country,
  region,
  sellerId,
  pageSize = PAGE_SIZE,
  page,
  targetMargin,
  incoterm = Incoterms.EXW,
  excludeCountry,
  minGrade,
  maxGrade,
}: {
  demandSignals?: FullDemandSignalDTO[];
  accountManagerIds?: number[];
  country?: Country;
  excludeCountry?: Country;
  region?: number;
  sellerId?: number;
  pageSize?: number;
  page?: number;
  targetMargin?: number;
  incoterm?: Incoterms;
  minGrade?: number;
  maxGrade?: number;
}) => {
  const effectiveTargetMargin = targetMargin ?? DEFAULT_TARGET_MARGIN;
  const [materialsWithOffers, setMaterialsWithOffers] = useState<MatchedMaterialWithOffers[]>([]);

  const {
    data: matchedMaterials,
    isLoading: isLoadingMaterials,
    isFetched: isFetchedMaterials,
    ...rest
  } = useApiTradeV2DemandSignalsBulkMatchSiteMaterialsList<MatchedMaterialsResponse>(
    {
      demand_signal_ids: demandSignals?.map((signal) => signal.id) || [],
      exclude_country: excludeCountry,
      max_grade_id: maxGrade,
      min_grade_id: minGrade,
      page,
      page_size: pageSize,
      recent_deals_lookback_days: DEFAULT_RECENT_DEALS_LOOKBACK_DAYS,
      recent_offers_lookback_days: DEFAULT_RECENT_OFFERS_LOOKBACK_DAYS,
      recent_signals_lookback_days: DEFAULT_RECENT_SIGNALS_LOOKBACK_DAYS,
      seller_am_ids: accountManagerIds,
      seller_id: sellerId,
      site_material_country: country,
      site_material_region_id: region,
    },
    {
      query: {
        enabled: !!demandSignals?.length,
        select(data: PaginatedMatchedSiteMaterialV2List): MatchedMaterialsResponse {
          return {
            ...data,
            results: transformPaginatedMatchedSiteMaterial(data, demandSignals),
          };
        },
      },
    }
  );

  const { mutateAsync: computeSellerPrice, isPending: isSellerOfferPending } =
    useApiTradeV1DemandSignalsCalculateSellPriceCreate({
      mutation: {
        onError: (error) => {
          logger.error('Error calculating seller price', error);
        },
      },
    });

  const { mutateAsync: computeBuyerPrice, isPending: isBuyerOfferPending } =
    useApiTradeV1SupplySignalCalculateBuyPriceCreate({
      mutation: {
        onError: (error) => {
          logger.error('Error calculating buyer price', error);
        },
      },
    });

  const { mutateAsync: computeOptimalOffers, isPending: isOptimalOffersPending } =
    useApiTradeV1DemandSignalsCalculateOptimalPriceCreate({
      mutation: {
        onError: (error) => {
          logger.error('Error calculating optimal price', error);
        },
      },
    });

  const getTonnesPerLoad = useCallback((siteMaterial: MatchedSiteMaterialV2) => {
    return Number(
      siteMaterial.default_payload_size || siteMaterial.material_spec.default_payload_size || DEFAULT_PAYLOAD_SIZE
    );
  }, []);

  const computeBuyerAndSellerPrices = useCallback(
    async (
      siteMaterial: MatchedSiteMaterialV2,
      demandSignalsToProcess: FullDemandSignalDTO[],
      supplySignal?: FullSupplySignalDTO
    ): Promise<SiteMaterialDemandSignalMatch[]> => {
      if (!demandSignalsToProcess.length) return [];

      try {
        const demandSignalIds = demandSignalsToProcess.map((signal) => signal.id);
        const tonnesPerLoad = getTonnesPerLoad(siteMaterial);
        const totalTonnes = (DEFAULT_NUMBER_OF_LOADS * tonnesPerLoad).toString();

        const [sellerOffers, buyerOffers] = await Promise.all([
          computeSellerPrice({
            data: {
              demand_signal_ids: demandSignalIds,
              seller_incoterm: incoterm,
              site_material_ids: [siteMaterial.id],
              target_margin: effectiveTargetMargin.toString(),
              tonnes: totalTonnes,
              tonnes_per_load: tonnesPerLoad.toString(),
            },
          }),
          supplySignal
            ? Promise.all(
                demandSignalsToProcess
                  .filter((signal) => signal.buyer)
                  .map((demandSignal) =>
                    computeBuyerPrice({
                      data: {
                        buyer_id: demandSignal.buyer!.id,
                        buyer_incoterm: demandSignal.incoterm,
                        port_of_discharge_id: demandSignal.port_of_discharge?.id,
                        supply_signal_ids: [supplySignal.id],
                        target_margin: effectiveTargetMargin.toString(),
                        tonnes: totalTonnes,
                      },
                    })
                      .then((result) => ({
                        buyerOffer: result[0],
                        demandSignalId: demandSignal.id,
                      }))
                      .catch((error) => {
                        logger.error(
                          `Error calculating buyer price for demand signal ${demandSignal.id}`,
                          error as Error
                        );
                        return null;
                      })
                  )
              )
            : Promise.resolve([]),
        ]);

        if (!sellerOffers.length) return [];

        // Create maps for faster lookups
        const sellerOffersByDemandSignalId = new Map(sellerOffers.map((offer) => [offer.demand_signal_id, offer]));

        const buyerOffersByDemandSignalId = new Map(
          buyerOffers.filter(Boolean).map((result) => [result!.demandSignalId, result!.buyerOffer])
        );

        // Combine the results
        return demandSignalsToProcess.map((demandSignal) => {
          const sellerOffer = sellerOffersByDemandSignalId.get(demandSignal.id);
          const buyerOffer = buyerOffersByDemandSignalId.get(demandSignal.id);

          return {
            buyerOffer,
            demandSignal,
            sellerOffer,
            siteMaterial,
            supplySignal,
            targetMargin: effectiveTargetMargin,
          };
        });
      } catch (error) {
        logger.error('Error in computeBuyerAndSellerPrices', error as Error);
        return demandSignalsToProcess.map((demandSignal) => ({
          demandSignal,
          siteMaterial,
          supplySignal,
          targetMargin: effectiveTargetMargin,
        }));
      }
    },
    [computeBuyerPrice, computeSellerPrice, getTonnesPerLoad, incoterm, effectiveTargetMargin]
  );

  const calculateOffersForMaterial = useCallback(
    async (matchedMaterial: MatchedSiteMaterialWithSignals): Promise<MatchedMaterialWithOffers> => {
      const { siteMaterial, demandSignals } = matchedMaterial;
      const supplySignal = matchedMaterial?.siteMaterial.latest_supply_signal;

      if (!demandSignals.length) {
        return {
          isPending: false,
          offers: [],
          siteMaterial,
        };
      }

      try {
        let offers: SiteMaterialDemandSignalMatch[] = [];

        if (supplySignal) {
          try {
            const tonnesPerLoad = getTonnesPerLoad(siteMaterial);
            const optimalOffers = await computeOptimalOffers({
              data: {
                demand_signal_ids: demandSignals.map((signal) => signal.id),
                max_buyer_price_difference_percentage: MAX_BUYER_PRICE_DIFFERENCE_PERCENTAGE,
                max_seller_price_difference_percentage: MAX_SELLER_PRICE_DIFFERENCE_PERCENTAGE,
                seller_incoterm: incoterm,
                site_material_ids: [siteMaterial.id],
                target_margin: effectiveTargetMargin.toString(),
                tonnes: (DEFAULT_NUMBER_OF_LOADS * tonnesPerLoad).toString(),
                tonnes_per_load: tonnesPerLoad.toString(),
              },
            });

            const offersWithPrice = optimalOffers.filter((offer) => offer.price_details);

            if (offersWithPrice.length) {
              const optimalOfferMap = new Map(offersWithPrice.map((offer) => [offer.demand_signal_id, offer]));

              offers = demandSignals.map((demandSignal) => {
                const optimalOffer = optimalOfferMap.get(demandSignal.id);
                return {
                  buyerOffer: optimalOffer
                    ? {
                        ...optimalOffer,
                        supply_signal_id: supplySignal.id,
                      }
                    : undefined,
                  demandSignal,
                  sellerOffer: optimalOffer,
                  siteMaterial,
                  supplySignal,
                  targetMargin: effectiveTargetMargin,
                };
              });

              const demandSignalsWithoutOffer = demandSignals.filter((signal) => !optimalOfferMap.has(signal.id));

              if (demandSignalsWithoutOffer.length) {
                const individualOffers = await computeBuyerAndSellerPrices(
                  siteMaterial,
                  demandSignalsWithoutOffer,
                  supplySignal
                );

                const individualOfferMap = new Map(individualOffers.map((offer) => [offer.demandSignal.id, offer]));

                offers = offers.map((offer) => {
                  if (offer.sellerOffer) return offer;
                  return individualOfferMap.get(offer.demandSignal.id) || offer;
                });
              }
            } else {
              offers = await computeBuyerAndSellerPrices(siteMaterial, demandSignals, supplySignal);
            }
          } catch (error) {
            logger.error('Error calculating optimal offers', error as Error);
            offers = await computeBuyerAndSellerPrices(siteMaterial, demandSignals, supplySignal);
          }
        } else {
          offers = await computeBuyerAndSellerPrices(siteMaterial, demandSignals);
        }

        return {
          isPending: false,
          offers,
          siteMaterial,
        };
      } catch (error) {
        logger.error('Error calculating offers for material', error as Error);
        return {
          isPending: false,
          offers: demandSignals.map((demandSignal) => ({
            demandSignal,
            siteMaterial,
            targetMargin: effectiveTargetMargin,
          })),
          siteMaterial,
        };
      }
    },
    [computeOptimalOffers, computeBuyerAndSellerPrices, getTonnesPerLoad, incoterm, effectiveTargetMargin]
  );

  useEffect(() => {
    const calculateAllOffers = async () => {
      if (!matchedMaterials?.results?.length || isLoadingMaterials) {
        return;
      }

      try {
        const materialsWithOffersResults = await Promise.all(matchedMaterials.results.map(calculateOffersForMaterial));

        setMaterialsWithOffers(materialsWithOffersResults);
      } catch (error) {
        logger.error('Error calculating offers for materials', error as Error);
      }
    };

    void calculateAllOffers();
  }, [matchedMaterials, isLoadingMaterials, calculateOffersForMaterial]);

  const isCalculatingOffers = isSellerOfferPending || isBuyerOfferPending || isOptimalOffersPending;

  const isPending = isLoadingMaterials || isCalculatingOffers;

  const allOffersFlattened = useMemo(
    () => materialsWithOffers.flatMap((material) => material.offers).sort(sortOffersByPriceDelta),
    [materialsWithOffers]
  );

  const isFetched = isFetchedMaterials && !isCalculatingOffers && materialsWithOffers.length;

  return {
    allOffers: allOffersFlattened,
    isFetched,
    isLoading: isPending,
    matchedMaterials,
    materialsWithOffers,
    ...rest,
  };
};
